import { useState, useCallback, useRef, type ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { PAGE_ROUTES } from '../constants/common';
import { translate } from '../localization';
import Modal from '../components/molecules/Modal';
import { imageGenerationSessionAtom } from '../store/imageGenerationSessionAtom';

/** Modal types for navigation guards */
type ModalType = 'start-new-session' | 'content-will-be-lost' | null;

export interface UseImageGenerationNavigationGuardReturn {
  /** Handle navigation with guard check for image generation route */
  handleImageGenerationNavigation: (
    onNavigate: (destination: string) => void
  ) => void;
  /** Handle navigation away from image generation page */
  handleNavigationAwayFromImageGeneration: (
    destination: string,
    onNavigate: (destination: string) => void
  ) => boolean;
  /** Render the navigation modal if open */
  renderImageGenerationModal: () => ReactElement | null;
  /** Whether a modal is currently open */
  isModalOpen: boolean;
}

/**
 * Hook that provides navigation guard for image generation page.
 *
 * Shows "Start New Session?" modal when:
 * - User is already on image generation page
 * - User has an active session (sessionId exists from generate prompt)
 * - User clicks the image generation icon in sidebar
 *
 * Shows "Content Will Be Lost" modal when:
 * - User is on image generation page with active session
 * - User tries to navigate to a different page via sidebar
 *
 * On confirm: Increments resetRequestCounter which ImageGenerationPage listens to
 */
export function useImageGenerationNavigationGuard(): UseImageGenerationNavigationGuardReturn {
  const location = useLocation();
  const [sessionState, setSessionState] = useAtom(imageGenerationSessionAtom);
  const [modalType, setModalType] = useState<ModalType>(null);

  // Store pending navigation info for "content will be lost" flow
  const pendingNavigation = useRef<{
    destination: string;
    onNavigate: (destination: string) => void;
  } | null>(null);

  /**
   * Check if currently on image generation route
   */
  const isOnImageGenerationRoute = useCallback(() => {
    return location.pathname.startsWith(PAGE_ROUTES.IMAGE_GENERATION);
  }, [location.pathname]);

  /**
   * Handle navigation to image generation with guard check
   */
  const handleImageGenerationNavigation = useCallback(
    (onNavigate: (destination: string) => void) => {
      // If already on image generation route AND has active session, show modal
      if (isOnImageGenerationRoute() && sessionState.hasActiveSession) {
        setModalType('start-new-session');
        return;
      }

      // Otherwise, just navigate normally
      onNavigate(PAGE_ROUTES.IMAGE_GENERATION);
    },
    [isOnImageGenerationRoute, sessionState.hasActiveSession]
  );

  /**
   * Handle navigation away from image generation page.
   * Shows "Content Will Be Lost" modal if user has active session.
   * @returns true if navigation was intercepted (modal shown), false if navigation should proceed
   */
  const handleNavigationAwayFromImageGeneration = useCallback(
    (
      destination: string,
      onNavigate: (destination: string) => void
    ): boolean => {
      // Only intercept if on image generation route with active session
      // and navigating to a different page
      if (
        isOnImageGenerationRoute() &&
        sessionState.hasActiveSession &&
        !destination.startsWith(PAGE_ROUTES.IMAGE_GENERATION)
      ) {
        pendingNavigation.current = { destination, onNavigate };
        setModalType('content-will-be-lost');
        return true;
      }

      return false;
    },
    [isOnImageGenerationRoute, sessionState.hasActiveSession]
  );

  /**
   * Handle modal cancel - close modal and stay on page
   */
  const handleCancel = useCallback(() => {
    setModalType(null);
    pendingNavigation.current = null;
  }, []);

  /**
   * Handle modal continue for "start new session" - signal reset to ImageGenerationPage
   */
  const handleStartNewSessionContinue = useCallback(() => {
    // Increment the reset counter to signal ImageGenerationPage to reset
    setSessionState(prev => ({
      ...prev,
      resetRequestCounter: prev.resetRequestCounter + 1,
      hasActiveSession: false,
    }));

    // Close modal
    setModalType(null);
  }, [setSessionState]);

  /**
   * Handle modal continue for "content will be lost" - clear state and navigate away
   */
  const handleContentWillBeLostContinue = useCallback(() => {
    // Clear all image generation state
    setSessionState({
      hasActiveSession: false,
      resetRequestCounter: 0,
    });

    // Close modal and navigate
    setModalType(null);

    if (pendingNavigation.current) {
      const { destination, onNavigate } = pendingNavigation.current;
      pendingNavigation.current = null;
      onNavigate(destination);
    }
  }, [setSessionState]);

  /**
   * Render the navigation modal based on modal type
   */
  const renderImageGenerationModal = useCallback(() => {
    if (!modalType) return null;

    if (modalType === 'start-new-session') {
      return (
        <Modal
          title={translate('agentic_start_new_session_title')}
          description='Starting a new session will clear your current prompt and generated images. Do you want to continue?'
          secondaryCTALabel={translate('common_cancel')}
          primaryCTALabel={translate('common_continue')}
          onSecondaryCTAClick={handleCancel}
          onPrimaryCTAClick={handleStartNewSessionContinue}
          onClose={handleCancel}
        />
      );
    }

    if (modalType === 'content-will-be-lost') {
      return (
        <Modal
          title={translate('tab_switch_data_loss_title')}
          description={translate('tab_switch_data_loss_image_message')}
          secondaryCTALabel={translate('common_cancel')}
          primaryCTALabel={translate('common_continue')}
          onSecondaryCTAClick={handleCancel}
          onPrimaryCTAClick={handleContentWillBeLostContinue}
          onClose={handleCancel}
        />
      );
    }

    return null;
  }, [
    modalType,
    handleCancel,
    handleStartNewSessionContinue,
    handleContentWillBeLostContinue,
  ]);

  return {
    handleImageGenerationNavigation,
    handleNavigationAwayFromImageGeneration,
    renderImageGenerationModal,
    isModalOpen: modalType !== null,
  };
}
